import { useState, useEffect, useRef, useCallback, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as AddCircleIcon } from './assets/addCircle.svg';
import debounce from 'lodash.debounce';
import { API } from '../../API';
import styles from './CasePicker.module.scss';

export interface CaseOption {
  value: number;
  label: string;
}

interface CasePickerProps {
  selectedIds: Set<number>;
  onAdd: (option: CaseOption) => void;
}

function PlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}


const SKELETON_WIDTHS = ['72%', '88%', '55%', '80%', '65%'];

function SkeletonList() {
  return (
    <>
      {SKELETON_WIDTHS.map((w, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={i} className={styles.skeletonItem} aria-hidden="true">
          <span className={styles.skeletonText} style={{ width: w }} />
          <span className={styles.skeletonCircle} />
        </li>
      ))}
    </>
  );
}

const LIST_ID = 'case-picker-list';

export default function CasePicker({ selectedIds, onAdd }: CasePickerProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<CaseOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchOptions = useCallback(
    debounce(async (q: string) => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams({ size: '50', page: '1', is_threat: 'false', has_project: 'false' });
        if (q) params.set('q', q);
        const data = await API.fetch('GET', `/API/v1/narratives?${params}`);
        setOptions((data?.objects ?? []).map((n: any) => ({ label: n.name as string, value: n.id as number })));
      } catch {
        setOptions([]);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    [],
  );

  useEffect(() => {
    if (isOpen) fetchOptions(query);
  }, [isOpen, query, fetchOptions]);

  useEffect(() => {
    if (isOpen) searchRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const open = () => {
    setIsOpen(true);
    setQuery('');
    setActiveIndex(-1);
    setIsLoading(true);
  };

  const close = () => {
    setIsOpen(false);
    buttonRef.current?.focus();
  };

  const filteredOptions = options.filter((o) => !selectedIds.has(o.value));

  const handleAdd = (option: CaseOption) => {
    onAdd(option);
    searchRef.current?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      close();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filteredOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleAdd(filteredOptions[activeIndex]);
    }
  };

  return (
    <div ref={containerRef} className={styles.container}>
      <button
        ref={buttonRef}
        type="button"
        className={styles.trigger}
        onClick={() => (isOpen ? close() : open())}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={LIST_ID}
      >
        <PlusIcon />
        <span>{t('Add case')}</span>
        <ChevronDownIcon />
      </button>

      {isOpen && (
        <div className={styles.dropdown} role="dialog" aria-label={t('Add cases')}>
          <div className={styles.searchWrap}>
            <input
              ref={searchRef}
              type="search"
              className={styles.search}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIndex(-1);
              }}
              onKeyDown={handleKeyDown}
              placeholder={t('Search for cases')}
              aria-label={t('Search for cases')}
              aria-autocomplete="list"
              aria-controls={LIST_ID}
              aria-activedescendant={
                activeIndex >= 0 ? `case-opt-${filteredOptions[activeIndex]?.value}` : undefined
              }
            />
          </div>

          <ul id={LIST_ID} role="listbox" aria-label={t('Cases')} aria-busy={isLoading} className={styles.list}>
            {isLoading && <SkeletonList />}
            {!isLoading && filteredOptions.length === 0 && (
              <li className={styles.status}>{t('No cases found')}</li>
            )}
            {!isLoading &&
              filteredOptions.map((opt, i) => (
                <li
                  key={opt.value}
                  id={`case-opt-${opt.value}`}
                  role="option"
                  aria-selected={false}
                  className={`${styles.item} ${i === activeIndex ? styles.itemActive : ''}`}
                  onClick={() => handleAdd(opt)}
                  
                >
                  <span className={styles.itemLabel}>{opt.label}</span>
                  <button
                    type="button"
                    className={styles.addBtn}
                    aria-label={t('Add {{name}}', { name: opt.label })}
                    tabIndex={-1}
                  >
                    <AddCircleIcon />
                  </button>
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
}
